// Die Mitspieler als Ansicht (task-016, docs/architektur-paket-2.md, Abschnitt „Client“): je Mitspieler
// eine Figur aus render/avatarModel.ts in seinen Farben, mit Namensschild (render/signs.ts) und
// Sprechblase (render/bubbles.ts) darüber. Wo er steht, sagt game/remotePlayers.ts; die Glieder pendeln
// über die gezeichnete Strecke (render/gang.ts), wie beim eigenen Avatar und bei der Kuh.
//
// In den Three.js-Objekten liegt kein Spielzustand (Architektur-Regel 1), nur, was zuletzt gezeichnet
// wurde. Mitspieler sind für die eigene Physik keine Hindernisse: Diese Ansicht gibt der Simulation
// nichts zurück, und `stepSimulation` kennt sie nicht – Avatare gehen durcheinander hindurch.
import type { Scene } from 'three';
import type { AvatarColors } from '@bauernhof/shared';
import type { MitspielerStand } from '../game/remotePlayers.js';
import { createAvatarModel, type AvatarModel } from './avatarModel.js';
import { createSprechblase, type Sprechblase } from './bubbles.js';
import { createGang, type Gang } from './gang.js';
import { SICHTWEITE, type Grundriss } from './sichtweite.js';
import { createNamensschild, type Namensschild } from './signs.js';

export interface MitspielerAnsicht {
  /**
   * Bringt die Figuren auf den Stand eines Bildes: neue anlegen, verschwundene abräumen, Ort, Blick,
   * Gang, Farben, Name und Satz setzen. `dt` sind die Sekunden seit dem letzten Bild.
   */
  sync(staende: readonly MitspielerStand[], satzVon: (id: number) => string | null, dt: number, kamera: Grundriss): void;
  /** Wie viele Figuren gerade in der Szene stehen. */
  anzahl(): number;
  dispose(): void;
}

interface Figur {
  modell: AvatarModel;
  gang: Gang;
  schild: Namensschild;
  blase: Sprechblase;
  /** Die zuletzt gesetzten Farben und der Name; neu gesetzt wird nur bei Änderung. */
  farben: string;
  name: string;
}

export function createMitspielerAnsicht(scene: Scene): MitspielerAnsicht {
  const figuren = new Map<number, Figur>();

  const baue = (stand: MitspielerStand): Figur => {
    const modell = createAvatarModel(stand.avatar);
    const schild = createNamensschild(stand.name);
    const blase = createSprechblase();
    modell.object.name = `mitspieler ${stand.id}`;
    modell.object.add(schild.object, blase.object);
    scene.add(modell.object);
    return { modell, gang: createGang(), schild, blase, farben: farbSchluessel(stand.avatar), name: stand.name };
  };

  const raeumeAb = (id: number, figur: Figur): void => {
    figur.schild.dispose();
    figur.blase.dispose();
    figur.modell.dispose();
    figuren.delete(id);
  };

  return {
    sync(staende, satzVon, dt, kamera) {
      const gesehen = new Set<number>();
      for (const stand of staende) {
        gesehen.add(stand.id);
        let figur = figuren.get(stand.id);
        if (figur === undefined) {
          figur = baue(stand);
          figuren.set(stand.id, figur);
        }
        const farben = farbSchluessel(stand.avatar);
        if (farben !== figur.farben) {
          figur.modell.setFarben(stand.avatar);
          figur.farben = farben;
        }
        if (stand.name !== figur.name) {
          figur.schild.setName(stand.name);
          figur.name = stand.name;
        }
        const { object } = figur.modell;
        object.position.set(stand.pos.x, stand.pos.y, stand.pos.z);
        object.rotation.y = stand.yaw;
        object.visible = Math.hypot(stand.pos.x - kamera.x, stand.pos.z - kamera.z) <= SICHTWEITE;
        figur.modell.setSwing(figur.gang.schritt(stand.pos.x, stand.pos.z, dt));
        figur.blase.setText(satzVon(stand.id));
      }
      for (const [id, figur] of figuren) {
        if (!gesehen.has(id)) raeumeAb(id, figur);
      }
    },

    anzahl: () => figuren.size,

    dispose() {
      for (const [id, figur] of figuren) raeumeAb(id, figur);
    },
  };
}

function farbSchluessel(farben: AvatarColors): string {
  return `${farben.haut}${farben.haar}${farben.oberteil}${farben.hose}`;
}
